import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Paper, Snackbar, Alert } from '@mui/material';
import PageHeader from '../components/common/PageHeader';
import LoadingSpinner from '../components/common/LoadingSpinner';
import UserForm from '../components/users/UserForm';
import { getUserById, updateUser } from '../services/userService';

const EditUserPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const { data } = await getUserById(id);
        setUser(data.data?.user || null);
      } catch (err) {
        console.error('Failed to fetch user:', err);
        setSnackbar({
          open: true,
          message: 'Failed to load user details',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchUser();
  }, [id]);

  const handleSubmit = async (formData) => {
    try {
      setSaving(true);
      await updateUser(id, formData);
      setSnackbar({
        open: true,
        message: 'User updated successfully',
        severity: 'success',
      });
      setTimeout(() => navigate('/users'), 1500);
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Failed to update user';
      setSnackbar({ open: true, message: errMsg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner fullPage />;

  return (
    <Box>
      <PageHeader
        title={`Edit User - ${user?.name || ''}`}
        breadcrumbs={[
          { label: 'Users', link: '/users' },
          { label: 'Edit' },
        ]}
      />

      <Paper sx={{ p: 3 }}>
        {user && (
          <UserForm
            defaultValues={{
              name: user.name || '',
              email: user.email || '',
              phone: user.phone || '',
              role: user.role || '',
              managerId: user.managerId || '',
              bankName: user.bankName || '',
              bankBranch: user.bankBranch || '',
            }}
            onSubmit={handleSubmit}
            loading={saving}
            isEdit
          />
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditUserPage;
